import { useCallback } from 'react';
import { noop } from 'lodash';
import type { ApolloError } from '@frameio/graphql-client';
import {
  useVersionAssetMutation,
  VersionedAsset,
} from '@frameio/graphql-client/generated';
import { isInteractiveLiveAssetType } from '@frameio/shared/media-viewer/hooks/useLiveUrl';
import { useLoadingToast } from '@frameio/shared/resources';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { startEvent } from '@frameio/telemetry';
import { CoreAssetsEventNames } from '@frameio/telemetry/events/analytics/core-assets';
import { log } from '@frameio/utils';
import { AssetParent } from '../../shared/gql/generated/AssetParent';
import { removeAssetsFromFolderAssets } from '../cache-ops';
import TEXT from './generated/TEXT/useVersionAsset';

type VersionAssetOpts = {
  onSuccess?: () => void;
  onError?: () => void;
};

type VersionAssetReturn = (fromAssetId: string, toAssetId: string) => void;

const DEFAULT_OPTS: VersionAssetOpts = {
  onSuccess: noop,
  onError: noop,
};

/**
 * NOTE: VERY IMPORTANT!
 *
 * This hook is only meant to be used for Finder 2. Assets are cached differently
 * in Finder 2 than in Finder 1, and the difference is big enough to be worth
 * splitting at this level, versus piping a feature flag around the old operation
 * hook.
 */
export function useVersionAsset(opts?: VersionAssetOpts): VersionAssetReturn {
  const { onError, onSuccess } = {
    ...DEFAULT_OPTS,
    ...opts,
  };

  const { addToast, updateAndHideToast } = useLoadingToast();

  const [versionAsset, { client }] = useVersionAssetMutation();
  const track = useTrackUnsafe();

  return useCallback(
    (fromAssetId: string, toAssetId: string) => {
      const fromAsset = client.cache.readFragment({
        fragment: VersionedAsset,
        fragmentName: 'VersionedAsset',
        id: `Asset:${fromAssetId}`,
        returnPartialData: true,
      });

      if (!fromAsset || fromAsset.versionNumber) return;

      const toAsset = client.cache.readFragment({
        fragment: AssetParent,
        fragmentName: 'AssetParent',
        id: `Asset:${toAssetId}`,
        returnPartialData: true,
      });

      if (!toAsset || !toAsset.parent || !toAsset.project) return;

      // Live URL assets must never enter a version stack (RICH-1696). Every
      // caller already guards this; this is a last-resort backstop in case a
      // future caller forgets.
      if (
        isInteractiveLiveAssetType(fromAsset.__typename) ||
        isInteractiveLiveAssetType(toAsset.__typename)
      ) {
        log.error(
          'Version Stack Creation Blocked',
          'attempted to version a live URL asset'
        );
        return;
      }

      const addingToExistingStack =
        toAsset.parent.__typename === 'VersionStackAsset';

      const toAssetWithVersion = client.cache.readFragment({
        fragment: VersionedAsset,
        fragmentName: 'VersionedAsset',
        id: `Asset:${toAsset.id}`,
        returnPartialData: true,
      });

      const targetContainer = addingToExistingStack
        ? client.cache.readFragment({
            fragment: VersionedAsset,
            fragmentName: 'VersionedAsset',
            id: `Asset:${toAsset.parent.id}`,
            returnPartialData: true,
          })
        : toAssetWithVersion;

      if (!toAssetWithVersion || !targetContainer) return;

      const createEvent = startEvent(CoreAssetsEventNames.CreateVersionStack, {
        from_asset_id: fromAssetId,
        to_asset_id: toAssetId,
        parent_folder_id: toAsset.parent.id,
        project_id: toAsset.project.id,
      });

      const toastId = addToast(TEXT.loading);

      track(
        'asset-version-stacked',
        { assets_count: 1 },
        { logToDatadog: true }
      );

      versionAsset({
        variables: {
          input: {
            firstAssetId: fromAssetId,
            secondAssetId: targetContainer.id,
          },
        },

        onCompleted: async () => {
          updateAndHideToast(toastId, {
            title: TEXT.successMessage,
            variant: 'success',
          });
          createEvent.completed();
          onSuccess?.();
        },
        onError(e: ApolloError) {
          createEvent.failed({ error: e });

          updateAndHideToast(toastId, {
            title: TEXT.errorMessage,
            variant: 'error',
          });
          log.error('Version Stack Creation Failed', e?.name, e?.message);
          onError?.();
        },
        update(proxy, result) {
          if (!result || result?.errors || !fromAsset.parent) return;
          const folderId = fromAsset.parent.id;

          // leaving this here since it feels much better in the UI after dragging
          // and dropping
          if (folderId)
            removeAssetsFromFolderAssets(proxy, folderId, [toAsset]);
        },
      });
    },
    [
      addToast,
      client.cache,
      onError,
      onSuccess,
      track,
      updateAndHideToast,
      versionAsset,
    ]
  );
}

TEXT.DEFINE({
  errorMessage: 'Couldn’t add new version',
  loading: 'Adding new version…',
  successMessage: 'Version added successfully',
});
